import { z } from 'zod';

// Schemas hold strings only (form-friendly). Empty-string-to-null coercion
// and any number parsing happens in onSubmit handlers, so the form's input
// type matches the schema's output type — keeps react-hook-form generics
// simple (no 3-generic useForm needed).

// Anchored, bounded char classes — linear time, no catastrophic backtracking.
// eslint-disable-next-line security/detect-unsafe-regex -- false positive on this shape
const slugRegex = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const passwordField = z
  .string()
  .refine(
    (v) => v === '' || v.length >= 8,
    'Password must be at least 8 characters (or leave blank to skip provisioning)',
  );

export const createInstituteSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(120),
  slug: z
    .string()
    .trim()
    .min(2)
    .max(60)
    .regex(slugRegex, 'Lowercase letters, digits, and hyphens only'),
  owner_email: z.string().trim().toLowerCase().email('Enter a valid email'),
  owner_password: passwordField,
});
export type CreateInstituteInput = z.infer<typeof createInstituteSchema>;

export const INSTITUTE_ROLES = ['owner', 'admin', 'teacher', 'examiner'] as const;
export type InstituteRoleValue = (typeof INSTITUTE_ROLES)[number];

export const addMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email'),
  role: z.enum(INSTITUTE_ROLES),
  password: passwordField,
});
export type AddMemberInput = z.infer<typeof addMemberSchema>;

export const EXAM_TYPES = ['academic', 'general'] as const;
export type ExamTypeValue = (typeof EXAM_TYPES)[number];

export const addStudentSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email'),
  exam_type: z.enum(EXAM_TYPES),
  roll_no: z.string().trim().max(40),
  target_band: z
    .string()
    .trim()
    .refine(
      (v) => v === '' || (!Number.isNaN(parseFloat(v)) && parseFloat(v) >= 0 && parseFloat(v) <= 9),
      'Target band must be between 0 and 9',
    ),
  password: passwordField,
});
export type AddStudentInput = z.infer<typeof addStudentSchema>;

export const addSuperadminSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email'),
  password: passwordField,
});
export type AddSuperadminInput = z.infer<typeof addSuperadminSchema>;

export const speakingRetentionSchema = z.object({
  retention_days: z
    .string()
    .trim()
    .refine((v) => {
      const n = Number(v);
      return v !== '' && Number.isInteger(n) && n >= 1 && n <= 365;
    }, 'Enter a whole number of days between 1 and 365'),
});
export type SpeakingRetentionInput = z.infer<typeof speakingRetentionSchema>;
